import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const ReadsContext = createContext({
  readIds: new Set(),
  isRead: () => false,
  markRead: () => {},
  markUnread: () => {},
});

export function ReadsProvider({ children }) {
  const { user } = useAuth();
  const [readIds, setReadIds] = useState(new Set());

  const refresh = useCallback(async () => {
    if (!user) { setReadIds(new Set()); return; }
    try {
      const r = await api.get("/reads");
      setReadIds(new Set(r.data.blog_ids || []));
    } catch { setReadIds(new Set()); }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const isRead = useCallback((blogId) => readIds.has(blogId), [readIds]);

  const markRead = useCallback(async (blogId) => {
    if (!user || !blogId || readIds.has(blogId)) return;
    // Optimistic
    setReadIds(prev => { const n = new Set(prev); n.add(blogId); return n; });
    try { await api.post(`/reads/${blogId}`); }
    catch { setReadIds(prev => { const n = new Set(prev); n.delete(blogId); return n; }); }
  }, [user, readIds]);

  const markUnread = useCallback(async (blogId) => {
    if (!user || !blogId || !readIds.has(blogId)) return;
    setReadIds(prev => { const n = new Set(prev); n.delete(blogId); return n; });
    try { await api.delete(`/reads/${blogId}`); }
    catch { setReadIds(prev => { const n = new Set(prev); n.add(blogId); return n; }); }
  }, [user, readIds]);

  return (
    <ReadsContext.Provider value={{ readIds, isRead, markRead, markUnread }}>
      {children}
    </ReadsContext.Provider>
  );
}

export const useReads = () => useContext(ReadsContext);
